import { Title } from "@solidjs/meta";
import { createSignal } from "solid-js";

import { saveTabletMarkup } from "~/api/treatment_presentations";
import { createPatientText, type PatientI18n, PatientTextProvider } from "~/patient_i18n/i18n";
import { PatientLanguageBar } from "~/patient_i18n/language_bar";
import {
  locationTreatmentPresentationImagePath,
  locationTreatmentPresentationLivePath,
  locationTreatmentPresentationVideoPath,
} from "~/routes";
import { DeckViewer } from "~/treatment_presentations/deck_viewer";
import { createLiveSlide } from "~/treatment_presentations/live_slide";
import { createMarkup } from "~/treatment_presentations/markup";
import { createLiveDrawings, createMarkupSaver } from "~/treatment_presentations/markup_saver";
import { frozenSrc, type PictureSrc, type PublicPageProps } from "~/treatment_presentations/types";

/** The deck a patient was texted, behind the identity check, or opened on the office tablet by the start code: the
 *  frozen copy and its frozen pictures, nothing live. On the tablet it keeps to the slide on the office's screen, and
 *  moving it there moves the screen. The chrome follows the patient's language, and the language bar's pick reaches
 *  the chart (the page is past the identity check); the deck reads as the office wrote it. */
export default function PublicTreatmentPresentationShow(
  props: PublicPageProps & { patientI18n: PatientI18n },
) {
  // The language only changes on a full page load, so the strings are read once.
  // eslint-disable-next-line solid/reactivity
  const text = createPatientText(props.patientI18n);
  const t = text.t;
  const [index, setIndex] = createSignal(0);
  // The tablet only: a patient's own phone moves nothing but itself, and draws nothing. Read once: the page shows one
  // copy for its life. On the tablet, lines drawn here save onto the show's drawing and show on the office's screen,
  // and theirs come here with the slide.
  // eslint-disable-next-line solid/reactivity
  const tablet = props.live;
  // A texted link sent with Include drawings draws the lines as sent, and takes none.
  // eslint-disable-next-line solid/reactivity
  const sent = tablet ? null : props.drawings;
  const markup = tablet || sent ? createMarkup() : undefined;
  if (markup && sent) markup.seed(sent);
  if (markup && tablet)
    createMarkupSaver(markup, (changes, keepalive) => saveTabletMarkup(props, changes, keepalive));
  const live = tablet
    ? createLiveSlide(
        // eslint-disable-next-line solid/reactivity
        locationTreatmentPresentationLivePath(props.locationId, props.snapshotId),
        (key) => {
          const at = props.deck.slides.findIndex((slide) => slide.key === key);
          if (at !== -1) setIndex(at);
        },
        markup && createLiveDrawings(markup),
      )
    : null;
  const move = (next: number) => {
    setIndex(next);
    const slide = props.deck.slides[next];
    if (slide) live?.send(slide.key);
  };
  const pictureSrc: PictureSrc = (picture) =>
    frozenSrc(picture, {
      image: (id) => locationTreatmentPresentationImagePath(props.locationId, props.snapshotId, id),
      video: (pageId) =>
        locationTreatmentPresentationVideoPath(props.locationId, props.snapshotId, pageId),
    });

  return (
    <PatientTextProvider value={text}>
      <Title>{t("treatment_presentation.title")}</Title>

      {/* The slide night color as a literal: the deck's --s-night token lives on the slide, not the page. */}
      <main class="flex min-h-dvh flex-col bg-[#08171D] py-6 sm:justify-center sm:py-12">
        <div class="mx-auto w-full max-w-5xl px-4">
          <PatientLanguageBar />
          {/* The copy never changes. A picture fails to load when the four-hour check runs out while the page is
              open, and a reload goes back through the check. */}
          <DeckViewer
            deck={props.deck}
            pictureSrc={pictureSrc}
            failedPictureText={t("treatment_presentation.failed_picture")}
            labels={{
              slides: t("treatment_presentation.slides"),
              back: t("treatment_presentation.back"),
              next: t("treatment_presentation.next"),
            }}
            // Lines sit on the slide's words, which only the fixed stage keeps in place.
            layout={markup ? "fixed" : "responsive"}
            markup={markup}
            drawing={tablet}
            drawLabels={{
              draw: t("treatment_presentation.draw.label"),
              tools: t("treatment_presentation.draw.tools"),
              pen: t("treatment_presentation.draw.pen"),
              highlighter: t("treatment_presentation.draw.highlighter"),
              eraser: t("treatment_presentation.draw.eraser"),
              color: t("treatment_presentation.draw.color"),
              colors: {
                red: t("treatment_presentation.draw.red"),
                yellow: t("treatment_presentation.draw.yellow"),
                green: t("treatment_presentation.draw.green"),
                blue: t("treatment_presentation.draw.blue"),
              },
              undo: t("treatment_presentation.draw.undo"),
              clear: t("treatment_presentation.draw.clear"),
              done: t("treatment_presentation.draw.done"),
            }}
            index={index()}
            onIndexChange={move}
            controls
            keyboard="document"
            dark
          />
        </div>
      </main>
    </PatientTextProvider>
  );
}
